import type { ReactNode } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { euQuery } from "@/web/src/consultas";

/**
 * O cabeçalho de Resultados: um título só para Relatório e Funil, com as duas
 * abas. São duas respostas para a mesma pergunta do dono — "está compensando?"
 * — e por isso moram no mesmo destino do menu. Cada aba continua com o seu
 * endereço (/relatorio, /funil) e a sua guarda de área; quem só pode ver uma,
 * vê uma aba só.
 */
export function CabecalhoResultados({ acao }: { acao?: ReactNode }) {
  const pathname = useRouterState({ select: (r) => r.location.pathname });
  const { data: eu } = useSuspenseQuery(euQuery());
  const abas = [
    { to: "/relatorio" as const, rotulo: "Relatório", area: "relatorio" },
    { to: "/funil" as const, rotulo: "Funil", area: "funil" },
  ].filter((a) => eu.areas.includes(a.area as (typeof eu.areas)[number]));

  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        <h1 className="text-[22px] font-bold tracking-tight md:text-[26px]">Resultados</h1>
        <p className="mt-1 max-w-2xl text-sm leading-relaxed text-[var(--color-suave)]">
          O que o assistente fez por você e quanto custa cada paciente que senta na cadeira. O gasto de mídia você informa; o resto sai
          das conversas e da agenda.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {abas.length > 1 && (
          <nav aria-label="Resultados" className="flex gap-0.5 rounded-full bg-[var(--color-painel)] p-[3px] shadow-[inset_0_0_0_1px_var(--color-borda)]">
            {abas.map((a) => {
              const ativa = pathname === a.to;
              return (
                <Link
                  key={a.to}
                  to={a.to}
                  aria-current={ativa ? "page" : undefined}
                  className={
                    "flex h-[30px] items-center rounded-full px-4 text-[13px] font-semibold transition-colors " +
                    (ativa ? "bg-[var(--color-cartao)] text-[var(--color-tinta)] shadow-botao" : "text-[var(--color-suave)] hover:text-[var(--color-tinta)]")
                  }
                >
                  {a.rotulo}
                </Link>
              );
            })}
          </nav>
        )}
        {acao}
      </div>
    </header>
  );
}

/** Um número com nome e explicação. `destaque` é o que o dono olha primeiro: o custo por quem apareceu. */
export function Kpi({ rotulo, valor, nota, destaque }: { rotulo: string; valor: ReactNode; nota: string; destaque?: boolean }) {
  return (
    <div
      className={
        "flex flex-col gap-1 rounded-[14px] p-4 " +
        (destaque ? "bg-[var(--color-marca)] text-[var(--color-sobre-marca)]" : "bg-[var(--color-cartao)] shadow-cartao")
      }
    >
      <span className={"text-[13px] font-semibold " + (destaque ? "opacity-85" : "text-[var(--color-suave)]")}>{rotulo}</span>
      <span className="num text-2xl font-bold tracking-tight">{valor}</span>
      <span className={"text-[12.5px] " + (destaque ? "opacity-85" : "text-[var(--color-fraco)]")}>{nota}</span>
    </div>
  );
}
